'use client';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sidebar } from 'primereact/sidebar';
import { useShipping, formatDaysLabel, DELIVERY_TIMELINE_NOTE } from '@/_components/shipping/useShipping';
import { getAuthUser } from '@/app/cart/_helpers';
import { DeliveryCheckSkeleton } from '@/_components/skeletons';
import { DeliveryFeedbackTrigger } from '@/_components/shipping/DeliveryFeedback';
import { FastestDeliveryCard } from '@/_components/shipping/ShippingEstimate';

/* ─── date label ──────────────────────────────────────────────── */
const friendlyLabel = (days) => {
    if (days == null) return '5–6 business days';
    if (days <= 1) return 'Tomorrow';
    const d = new Date();
    d.setDate(d.getDate() + Number(days));
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

/* ─── address list ─────────────────────────────────────────────── */
const AddressList = ({ loading, addresses, onSelect, onHide }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
        {loading && [1, 2, 3].map((i) => (
            <div key={i} style={{ height: '4rem', borderRadius: '10px', background: '#f1f5f9' }} />
        ))}
        {!loading && !addresses.length && (
            <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: '#94a3b8', fontSize: '0.82rem' }}>
                <i className="pi pi-map-marker" style={{ fontSize: '1.3rem', display: 'block', marginBottom: '0.4rem' }} />
                No saved addresses
            </div>
        )}
        {!loading && addresses.map((addr) => (
            <button
                key={addr._id}
                onClick={() => { onSelect(String(addr.pincode || addr.pinCode || '')); onHide(); }}
                style={{
                    width: '100%', border: '1.5px solid #e2e8f0',
                    borderRadius: '10px', background: 'white',
                    padding: '0.7rem 1rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
                    textAlign: 'left', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary-color)'; e.currentTarget.style.background = 'rgba(11,49,99,0.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
            >
                <span style={{
                    width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(11,49,99,0.08)', border: '1px solid rgba(11,49,99,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <i className="pi pi-home" style={{ fontSize: '0.72rem', color: 'var(--primary-color)' }} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.15rem' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {addr.fullName || 'Address'}
                        </span>
                        {addr.isDefault && (
                            <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#ccfbf1', color: '#0f766e', padding: '0.05rem 0.4rem', borderRadius: '999px', border: '1px solid #99f6e4', whiteSpace: 'nowrap' }}>
                                Default
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[addr.addressLine1, addr.city, addr.state].filter(Boolean).join(', ')}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--primary-color)', fontWeight: 600, marginTop: '0.1rem' }}>
                        PIN: {addr.pincode || addr.pinCode}
                    </div>
                </div>
                <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.4rem', flexShrink: 0 }} />
            </button>
        ))}
    </div>
);

/* ─── pincode input + result (shared by sidebar & mobile card) ─── */
const PincodeForm = ({ pincode, setPincode, loading, error, setError, result, checkPincode, clear, useCurrentLocation, detectingLocation }) => {
    const isServiceable = result?.serviceable;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

            {/* Input row */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div
                    style={{
                        display: 'flex', alignItems: 'center', flex: 1,
                        background: 'white',
                        border: `1.5px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
                        borderRadius: '8px', padding: '0 0.65rem', gap: '0.4rem',
                        transition: 'border-color 0.15s',
                    }}
                    onFocusCapture={(e) => (e.currentTarget.style.borderColor = 'var(--primary-color)')}
                    onBlurCapture={(e) => (e.currentTarget.style.borderColor = error ? '#fca5a5' : '#e2e8f0')}
                >
                    <i className="pi pi-map-marker" style={{ fontSize: '0.78rem', color: '#94a3b8' }} />
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={pincode}
                        placeholder="Enter 6-digit pincode"
                        onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setPincode(v);
                            setError(null);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && pincode.length === 6 && checkPincode()}
                        style={{
                            border: 'none', outline: 'none', background: 'transparent',
                            fontSize: '0.85rem', flex: 1, padding: '0.55rem 0',
                            color: '#1e293b', letterSpacing: '0.04em',
                        }}
                    />
                </div>
                {useCurrentLocation && (
                    <button
                        type="button"
                        onClick={useCurrentLocation}
                        disabled={detectingLocation}
                        title="Use my current location"
                        aria-label="Use my current location"
                        style={{
                            background: 'white',
                            border: '1.5px solid var(--primary-color)', borderRadius: '8px',
                            width: '2.15rem', height: '2.15rem', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: detectingLocation ? 'wait' : 'pointer',
                            color: 'var(--primary-color)',
                        }}
                    >
                        <i className={`pi ${detectingLocation ? 'pi-spin pi-spinner' : 'pi-map-marker'}`} style={{ fontSize: '0.85rem' }} />
                    </button>
                )}
                <button
                    onClick={() => checkPincode()}
                    disabled={pincode.length !== 6 || loading}
                    style={{
                        background: pincode.length === 6 && !loading ? 'var(--primary-color)' : '#e2e8f0',
                        color: pincode.length === 6 && !loading ? 'white' : '#94a3b8',
                        border: 'none', borderRadius: '8px',
                        padding: '0.55rem 1.1rem',
                        cursor: pincode.length === 6 && !loading ? 'pointer' : 'default',
                        fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap',
                        transition: 'all 0.15s', flexShrink: 0,
                    }}
                >
                    {loading ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem' }} /> : 'Check'}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.72rem', color: '#ef4444' }} />
                    <span style={{ fontSize: '0.72rem', color: '#ef4444' }}>{error}</span>
                </div>
            )}

            {/* Result */}
            {result && (
                <div style={{
                    borderRadius: '10px', padding: '0.85rem 1rem',
                    display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
                    ...(isServiceable
                        ? { background: '#f0fdf4', border: '1px solid #86efac' }
                        : { background: '#fff7ed', border: '1px solid #fdba74' }),
                }}>
                    <i
                        className={`pi ${isServiceable ? 'pi-check-circle' : 'pi-times-circle'} flex-shrink-0`}
                        style={{ fontSize: '1.1rem', marginTop: '0.1rem', color: isServiceable ? '#22c55e' : '#f97316' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {isServiceable ? (
                            <>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#15803d' }}>
                                    {result.estimatedDays != null ? 'Delivered in' : 'Delivered within'}{' '}
                                    {formatDaysLabel(result.estimatedDays)}
                                    {result.courier ? ` via ${result.courier}` : ''}
                                </div>
                                {result.deliveryDate && (
                                    <div style={{ fontSize: '0.78rem', color: '#166534', fontWeight: 500, marginTop: '0.15rem' }}>
                                        Expected by {result.deliveryDate}
                                    </div>
                                )}
                                <div style={{ fontSize: '0.7rem', color: '#4ade80', fontWeight: 500, marginTop: '0.1rem' }}>
                                    Pincode {result.pincode}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.35rem' }}>
                                    <i className="pi pi-info-circle" style={{ fontSize: '0.58rem', color: '#64748b', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.4 }}>
                                        {DELIVERY_TIMELINE_NOTE}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#c2410c' }}>
                                    Delivery not available
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#9a3412', marginTop: '0.1rem' }}>
                                    We don&apos;t deliver to pincode {result.pincode} currently.
                                </div>
                            </>
                        )}
                    </div>
                    <button
                        onClick={clear}
                        style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            padding: '0.1rem', color: '#94a3b8', flexShrink: 0,
                        }}
                        title="Clear"
                    >
                        <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                    </button>
                </div>
            )}

            {result && <FastestDeliveryCard fastestDelivery={result.fastestDelivery} />}
        </div>
    );
};

/* ─── desktop sidebar content ─────────────────────────────────── */
const DeliverySidebar = ({ visible, onHide, pincode, setPincode, loading, error, setError, result, checkPincode, clear, user, useCurrentLocation, detectingLocation }) => {
    const [addresses, setAddresses] = useState([]);
    const [addrLoading, setAddrLoading] = useState(false);

    useEffect(() => {
        if (!visible || !user) return;
        setAddrLoading(true);
        fetch('/api/user/address')
            .then((r) => r.json())
            .then((json) => {
                if (json.success) setAddresses(json.data?.addresses || json.data || []);
            })
            .catch(() => {})
            .finally(() => setAddrLoading(false));
    }, [visible, user]);

    const handleAddressSelect = (pin) => {
        setPincode(pin);
        checkPincode(pin);
        onHide();
    };

    return (
        <Sidebar
            visible={visible}
            position="right"
            onHide={onHide}
            showCloseIcon={false}
            style={{ width: '420px', maxWidth: '96vw' }}
            pt={{
                root:    { style: { maxWidth: '94vw' } },
                header:  { style: { padding: 0 } },
                content: { style: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } },
            }}
            header={
                <div style={{
                    background: 'var(--primary-color)',
                    padding: '1rem 1.2rem',
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className="pi pi-truck" style={{ color: '#fff', fontSize: '1rem' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                            Check Delivery
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', marginTop: '0.15rem' }}>
                            Enter pincode or pick a saved address
                        </div>
                    </div>
                    <button
                        onClick={onHide}
                        style={{
                            background: 'rgba(255,255,255,0.12)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            borderRadius: '50%', width: 30, height: 30, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                    >
                        <i className="pi pi-times" style={{ color: '#fff', fontSize: '0.7rem' }} />
                    </button>
                </div>
            }
        >
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>

                {/* Pincode check */}
                <div style={{
                    background: '#f8fafc', borderRadius: '12px',
                    padding: '1rem', border: '1px solid #e2e8f0',
                    marginBottom: user ? '1.25rem' : 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem' }}>
                        <i className="pi pi-search-location" style={{ fontSize: '0.78rem', color: 'var(--primary-color)' }} />
                        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                            Check by Pincode
                        </span>
                    </div>
                    <PincodeForm
                        pincode={pincode} setPincode={setPincode}
                        loading={loading} error={error} setError={setError}
                        result={result} checkPincode={checkPincode} clear={clear}
                        useCurrentLocation={useCurrentLocation} detectingLocation={detectingLocation}
                    />
                </div>

                {/* Saved addresses */}
                {user && (
                    <div>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            marginBottom: '0.75rem',
                        }}>
                            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                or pick a saved address
                            </span>
                            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                        </div>
                        <AddressList
                            loading={addrLoading}
                            addresses={addresses}
                            onSelect={handleAddressSelect}
                            onHide={onHide}
                        />
                    </div>
                )}

                {/* Bottom note */}
                <div style={{
                    marginTop: '1.5rem', padding: '0.75rem 1rem',
                    background: 'rgba(11,49,99,0.04)',
                    borderRadius: '8px', border: '1px solid rgba(11,49,99,0.1)',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '0.78rem', color: 'var(--primary-color)', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.5 }}>
                        Delivery times may vary for remote locations. Cash on delivery availability also depends on pincode.
                    </span>
                </div>
            </div>
        </Sidebar>
    );
};

/* ─── mobile bottom sheet ──────────────────────────────────────── */
const MobileDeliverySheet = ({ visible, onHide, pincode, setPincode, loading, error, setError, result, checkPincode, clear, user, useCurrentLocation, detectingLocation }) => {
    const [sheetIn, setSheetIn] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [addrLoading, setAddrLoading] = useState(false);

    useEffect(() => {
        if (!visible) { setSheetIn(false); return; }
        requestAnimationFrame(() => requestAnimationFrame(() => setSheetIn(true)));
        if (!user) return;
        setAddrLoading(true);
        fetch('/api/user/address')
            .then((r) => r.json())
            .then((json) => {
                if (json.success) setAddresses(json.data?.addresses || json.data || []);
            })
            .catch(() => {})
            .finally(() => setAddrLoading(false));
    }, [visible, user]);

    const handleHide = () => { setSheetIn(false); setTimeout(onHide, 320); };
    const handleAddressSelect = (pin) => {
        setPincode(pin);
        checkPincode(pin);
        handleHide();
    };

    if (!visible && !sheetIn) return null;
    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1300,
                background: sheetIn ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0)',
                transition: 'background 0.32s',
                backdropFilter: sheetIn ? 'blur(2px)' : 'none',
            }}
            onClick={handleHide}
        >
            <div
                style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    borderRadius: '20px 20px 0 0',
                    background: '#fff',
                    transform: sheetIn ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform 0.32s cubic-bezier(0.32,0.72,0,1)',
                    maxHeight: '85vh',
                    display: 'flex', flexDirection: 'column',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                    overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* drag handle */}
                <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.12)' }} />
                </div>

                {/* header */}
                <div style={{
                    background: 'var(--primary-color)',
                    padding: '0.9rem 1.2rem',
                    display: 'flex', alignItems: 'center', gap: '0.65rem',
                }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className="pi pi-truck" style={{ color: '#fff', fontSize: '0.9rem' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.92rem' }}>Check Delivery</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.68rem' }}>Enter pincode or pick saved address</div>
                    </div>
                    <button
                        onClick={handleHide}
                        style={{
                            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
                            borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                    >
                        <i className="pi pi-times" style={{ color: '#fff', fontSize: '0.65rem' }} />
                    </button>
                </div>

                {/* body */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
                    <PincodeForm
                        pincode={pincode} setPincode={setPincode}
                        loading={loading} error={error} setError={setError}
                        result={result} checkPincode={checkPincode} clear={clear}
                        useCurrentLocation={useCurrentLocation} detectingLocation={detectingLocation}
                    />
                    {user && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '1rem 0 0.75rem' }}>
                                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                                <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                    or pick a saved address
                                </span>
                                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                            </div>
                            <AddressList
                                loading={addrLoading}
                                addresses={addresses}
                                onSelect={handleAddressSelect}
                                onHide={handleHide}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

/* ─── desktop trigger row ─────────────────────────────────────── */
const DesktopTrigger = ({ result, onClick, pincode, feedbackNode }) => {
    const isServiceable = result?.serviceable;
    return (
        <button
            onClick={onClick}
            style={{
                width: '100%',
                background: '#fff',
                border: '1px solid rgba(11,49,99,0.18)',
                borderRadius: '10px',
                padding: '0.7rem 1rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                cursor: 'pointer', textAlign: 'left',
                marginTop: '0.75rem', marginBottom: '0.5rem',
                transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary-color)'; e.currentTarget.style.background = 'rgba(11,49,99,0.02)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(11,49,99,0.18)'; e.currentTarget.style.background = '#fff'; }}
        >
            {/* Icon */}
            <div style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                background: result
                    ? (isServiceable ? '#f0fdf4' : '#fff7ed')
                    : 'rgba(11,49,99,0.07)',
                border: `1.5px solid ${result ? (isServiceable ? '#86efac' : '#fdba74') : 'rgba(11,49,99,0.15)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <i
                    className={`pi ${result ? (isServiceable ? 'pi-check-circle' : 'pi-times-circle') : 'pi-truck'}`}
                    style={{ fontSize: '0.95rem', color: result ? (isServiceable ? '#22c55e' : '#f97316') : 'var(--primary-color)' }}
                />
            </div>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {result ? (
                    isServiceable ? (
                        <>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#15803d', lineHeight: 1.3 }}>
                                {result.estimatedDays != null ? 'Delivered in' : 'Delivered within'}{' '}
                                {formatDaysLabel(result.estimatedDays)}
                                {result.courier ? ` via ${result.courier}` : ''}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.1rem' }}>
                                Pincode {result.pincode} · <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>Change</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                                <i className="pi pi-info-circle" style={{ fontSize: '0.56rem', color: '#94a3b8', flexShrink: 0 }} />
                                <span style={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.3 }}>
                                    {DELIVERY_TIMELINE_NOTE}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#c2410c', lineHeight: 1.3 }}>
                                Delivery not available for {result.pincode}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.1rem' }}>
                                <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>Try a different pincode</span>
                            </div>
                        </>
                    )
                ) : (
                    <>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
                            Check Delivery
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.1rem' }}>
                            Enter pincode to see delivery date &amp; availability
                        </div>
                    </>
                )}
            </div>

            {/* Feedback + chevron */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                {feedbackNode}
            </div>
            <i className="pi pi-chevron-right" style={{ fontSize: '0.7rem', color: '#94a3b8', flexShrink: 0 }} />
        </button>
    );
};

/* ─── main component ───────────────────────────────────────────── */
export const ProductDeliveryCheck = () => {
    const { pincode, setPincode, result, loading, error, setError, checkPincode, clear, useCurrentLocation, detectingLocation, initializing } = useShipping();
    const [open,    setOpen]    = useState(false);
    const [user,    setUser]    = useState(null);
    const [mounted, setMounted] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        setMounted(true);
        setUser(getAuthUser());
        const update = () => setIsDesktop(window.innerWidth >= 1024);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    /* Once the account's saved delivery check has been fetched (or found to be
       empty), fall back to auto-checking the default address's pincode. */
    useEffect(() => {
        if (!user || initializing || result) return;
        fetch('/api/user/address')
            .then((r) => r.json())
            .then((json) => {
                if (!json.success) return;
                const list = json.data?.addresses || json.data || [];
                const def = list.find((a) => a.isDefault) || list[0];
                const pin = def?.pincode || def?.pinCode ? String(def.pincode || def.pinCode) : '';
                if (pin) checkPincode(pin);
            })
            .catch(() => {});
    }, [user, initializing]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!mounted) return <DeliveryCheckSkeleton />;

    const sharedProps = {
        visible: open,
        onHide: () => setOpen(false),
        pincode, setPincode, loading, error, setError,
        result, checkPincode, clear, user,
        useCurrentLocation, detectingLocation,
    };

    const feedbackNode = <DeliveryFeedbackTrigger pincode={pincode || result?.pincode} source="product_page" />;

    return (
        <>
            {/* ── Desktop: right sidebar ────────────────────────── */}
            {isDesktop && (
                <DeliverySidebar {...sharedProps} />
            )}

            {/* ── Mobile/tablet: bottom sheet ───────────────────── */}
            {!isDesktop && mounted && (
                <MobileDeliverySheet {...sharedProps} />
            )}

            {/* ── Desktop trigger row ───────────────────────────── */}
            <div className="hidden lg:block">
                <DesktopTrigger
                    result={result}
                    onClick={() => setOpen(true)}
                    pincode={pincode}
                    feedbackNode={feedbackNode}
                />
            </div>

            {/* ── Mobile/tablet inline trigger ─────────────────── */}
            <div className="block lg:hidden">
                <button
                    onClick={() => setOpen(true)}
                    style={{
                        width: '100%',
                        background: '#fff',
                        border: '1px solid rgba(11,49,99,0.18)',
                        borderRadius: '10px',
                        padding: '0.7rem 1rem',
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        cursor: 'pointer', textAlign: 'left',
                        marginTop: '0.75rem', marginBottom: '0.5rem',
                    }}
                >
                    <div style={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                        background: result
                            ? (result.serviceable ? '#f0fdf4' : '#fff7ed')
                            : 'rgba(11,49,99,0.07)',
                        border: `1.5px solid ${result ? (result.serviceable ? '#86efac' : '#fdba74') : 'rgba(11,49,99,0.15)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i
                            className={`pi ${result ? (result.serviceable ? 'pi-check-circle' : 'pi-times-circle') : 'pi-truck'}`}
                            style={{ fontSize: '0.88rem', color: result ? (result.serviceable ? '#22c55e' : '#f97316') : 'var(--primary-color)' }}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {result?.serviceable ? (
                            <>
                                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#15803d' }}>
                                    {formatDaysLabel(result.estimatedDays)} delivery
                                </div>
                                <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.1rem' }}>
                                    PIN {result.pincode} · <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>Change</span>
                                </div>
                            </>
                        ) : result ? (
                            <>
                                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#c2410c' }}>Not available for {result.pincode}</div>
                                <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.1rem' }}>Tap to try another pincode</div>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>Check Delivery</div>
                                <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.1rem' }}>Tap to enter your pincode</div>
                            </>
                        )}
                    </div>
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.68rem', color: '#94a3b8' }} />
                </button>
            </div>

        </>
    );
};
